import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from './Header';
import { LinktreeLogo } from './LinktreeLogo';
import { cn } from '../utils/twUtils';

interface BaseLayoutProps {
  heading?: ReactNode;
  subHeading?: ReactNode;
  headingClasses?: string;
  subHeadingClasses?: string;
  headerClasses?: string;
  mainClasses?: string;
  pageClasses?: string;
  contentWidth?: number | 'full';
  isMobileApp?: boolean;
  hideLogo?: boolean;
}

interface DefaultLayoutProps extends BaseLayoutProps {
  variant?: 'default';
  children: ReactNode;
}

interface InterstitialLayoutProps extends BaseLayoutProps {
  variant?: 'interstitial';
  children?: ReactNode;
}

interface BannerLayoutProps extends BaseLayoutProps {
  variant?: 'banner';
  children?: ReactNode;
  bannerImageDesktop?: string;
  backgroundColor?: string;
}

export type LayoutProps =
  DefaultLayoutProps | InterstitialLayoutProps | BannerLayoutProps;

interface ContentLayoutProps extends Omit<BaseLayoutProps, 'pageClasses'> {
  children?: ReactNode;
  variantForCentering?: 'default' | 'interstitial' | 'banner';
}

type DesktopBannerStatus = 'loading' | 'loaded' | 'not-needed';

declare global {
  interface Window {
    __ltDesktopBannerStatus?: DesktopBannerStatus;
  }
}

const DESKTOP_BANNER_MEDIA_QUERY = '(min-width: 1024px)';

const canMatchDesktopBannerMedia = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const publishDesktopBannerStatus = (status: DesktopBannerStatus): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.__ltDesktopBannerStatus = status;
  window.dispatchEvent(
    new CustomEvent('lt:desktop-banner-status', { detail: { status } })
  );
};

const useShouldRenderDesktopBanner = (enabled: boolean): boolean => {
  const [shouldRender, setShouldRender] = useState(() => {
    if (!enabled || !canMatchDesktopBannerMedia()) {
      return false;
    }

    return window.matchMedia(DESKTOP_BANNER_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (!enabled || !canMatchDesktopBannerMedia()) {
      setShouldRender(false);
      return undefined;
    }

    const mediaQuery = window.matchMedia(DESKTOP_BANNER_MEDIA_QUERY);
    const updateShouldRender = (event?: MediaQueryListEvent): void => {
      setShouldRender(event?.matches ?? mediaQuery.matches);
    };

    updateShouldRender();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateShouldRender);

      return () => {
        mediaQuery.removeEventListener('change', updateShouldRender);
      };
    }

    mediaQuery.addListener(updateShouldRender);

    return () => {
      mediaQuery.removeListener(updateShouldRender);
    };
  }, [enabled]);

  return shouldRender;
};

const ContentLayout = ({
  heading,
  subHeading,
  headingClasses,
  subHeadingClasses,
  headerClasses,
  mainClasses,
  contentWidth = 480,
  children,
  variantForCentering = 'default',
}: ContentLayoutProps): ReactElement => {
  return (
    <>
      <Header
        heading={heading}
        subHeading={subHeading}
        headingClasses={headingClasses}
        subHeadingClasses={subHeadingClasses}
        className={cn(
          headerClasses,
          'mx-auto w-full max-w-[480px] px-6 md:px-0'
        )}
      />
      {children && (
        <main
          className={cn(
            mainClasses,
            'mx-auto w-full flex-grow px-6 pb-[120px] pt-6 md:px-0',
            contentWidth === 'full'
              ? 'max-w-[948px]'
              : `max-w-[${contentWidth}px]`,
            { 'pb-[120px]': variantForCentering !== 'interstitial' }
          )}
        >
          {children}
        </main>
      )}
    </>
  );
};

const Layout = (props: LayoutProps): ReactElement => {
  const { t } = useTranslation();
  const {
    variant = 'default',
    pageClasses,
    children,
    isMobileApp,
    hideLogo = false,
    ...contentProps
  } = props;
  const hasDesktopBanner =
    !isMobileApp &&
    variant === 'banner' &&
    Boolean((props as BannerLayoutProps).bannerImageDesktop);
  const shouldRenderDesktopBanner =
    useShouldRenderDesktopBanner(hasDesktopBanner);

  useEffect(() => {
    publishDesktopBannerStatus(
      hasDesktopBanner && shouldRenderDesktopBanner ? 'loading' : 'not-needed'
    );
  }, [hasDesktopBanner, shouldRenderDesktopBanner]);

  const LogoElement = (
    <div className="block h-[24px] lg:h-[32px] absolute top-6 left-6 lg:top-10 lg:left-10 z-50">
      {isMobileApp ? (
        <LinktreeLogo height="100%" />
      ) : (
        <a
          href="/"
          aria-label={t('Linktree home')}
          className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
        >
          <LinktreeLogo height="100%" />
        </a>
      )}
    </div>
  );
  // Handle banner variant
  if (variant === 'banner') {
    const { bannerImageDesktop, backgroundColor = 'bg-[#225AC0]' } =
      props as BannerLayoutProps;

    return (
      <div
        className={cn(
          pageClasses,
          ' relative flex w-full',
          'h-screen overflow-hidden',
          !isMobileApp ? 'lg:flex-row' : 'flex-col'
        )}
      >
        {!hideLogo && LogoElement}
        <div
          className={cn(
            'flex w-full flex-shrink-0 flex-col',
            'h-full overflow-y-auto',
            !isMobileApp ? 'lg:w-1/2' : 'w-full'
          )}
        >
          <div className="pt-24 lg:pt-20 2xl:pt-32">
            <ContentLayout {...contentProps} variantForCentering="banner">
              {children}
            </ContentLayout>
          </div>
        </div>

        {shouldRenderDesktopBanner && bannerImageDesktop && (
          <div
            className={cn(
              'hidden lg:block lg:h-full lg:w-1/2 lg:flex-shrink-0',
              backgroundColor
            )}
          >
            <img
              src={bannerImageDesktop}
              alt={t('Promotional Banner')}
              fetchPriority="high"
              data-lt-lcp-banner="true"
              onLoad={() => publishDesktopBannerStatus('loaded')}
              onError={() => publishDesktopBannerStatus('loaded')}
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>
    );
  }

  // Handle default and interstitial variants
  return (
    <div
      className={cn(
        pageClasses,
        ' flex min-h-screen w-full flex-col pt-[28px] lg:pt-32 2xl:pt-64',
        {
          'place-content-center lg:place-content-start':
            variant === 'interstitial',
        }
      )}
    >
      {!hideLogo && LogoElement}
      <ContentLayout {...contentProps} variantForCentering={variant}>
        {children}
      </ContentLayout>
    </div>
  );
};

export default Layout;
